import { buildQuery } from '@calendly/http-tools'

/*
 * App URLs
 */

export const getScope = (type) => (type === 'page' ? 'team' : type)

export const appWorkflowListUrl = (workflowOwner, canAccessManagedWorkflows) => {
  if (!['organization', 'user_group'].includes(workflowOwner.type)) {
    return `/app/workflows/${getScope(workflowOwner.type)}/${workflowOwner.id}`
  }

  if (canAccessManagedWorkflows) return '/app/admin/managed_workflows'

  return '/app/workflows/user/me?admin_managed=true'
}

export const appWorkflowTemplatesListUrl = ({ id, type }) => {
  const scope = getScope(type)

  if (type === 'organization') return '/app/admin/managed_workflows/workflow_templates'

  return `/app/workflows/${scope}s/${id}/workflow_templates`
}

export const appNewWorkflowUrl = ({ type, id }) => {
  const scope = getScope(type)

  if (type === 'organization') return '/app/admin/managed_workflows/new'

  return `/app/workflows/${scope}s/${id}/workflows/new`
}

export const appCloneWorkflowUrl = (owner, originalWorkflowUuid) => (
  `${appNewWorkflowUrl(owner)}?${buildQuery({ originalWorkflowUuid })}`
)

export const appNewWorkflowFromTemplateUrl = (owner, templateId, backTo) => (
  `${appNewWorkflowUrl(owner)}?${buildQuery({ templateId, backTo })
  }`
)

export const appEditWorkflowUrl = ({ uuid, ownerType, ownerId }) => {
  if (ownerType === 'organization') return `/app/admin/managed_workflows/${uuid}/edit`
  if (ownerType === 'user_group') return `/app/admin/managed_workflows/user_groups/${ownerId}/workflows/${uuid}/edit`

  const scope = getScope(ownerType)
  return `/app/workflows/${scope}s/${ownerId}/workflows/${uuid}/edit`
}

const appViewManagedWorkflowUrl = (uuid) => `/app/workflows/organization/workflows/${uuid}`

export const appViewWorkflowUrl = ({ uuid, ownerType, ownerId, isAdminOrOwner, isGroupAdmin }) => {
  if (ownerType === 'organization' && !isAdminOrOwner) {
    return appViewManagedWorkflowUrl(uuid)
  }
  if (ownerType === 'user_group' && !isGroupAdmin && !isAdminOrOwner) {
    return `${appViewManagedWorkflowUrl(uuid)}?group_uuid=${ownerId}`
  }

  return appEditWorkflowUrl({ uuid, ownerType, ownerId })
}

/*
 * API Urls
 */

export const apiOwnerListLegacyUrl = '/api/workflows/owners'

export const apiOwnerListUrl = '/api/apps/workflows/owners'
export const apiManagedWorkflowsUrl = '/api/apps/workflows/managed_workflows'

export const apiCreateWorkflowUrl = (owner, { workflowsExtractionEnabled = false }) => {
  if (workflowsExtractionEnabled) {
    const query = owner.type === 'organization'
      ? { owner_type: 'organization' }
      : { owner_id: owner.id, owner_type: owner.type }
    return `/api/apps/workflows/workflows?${buildQuery(query)}`
  }

  return owner.type === 'organization'
    ? '/api/organization/workflows'
    : `/api/${owner.type}s/${owner.id}/workflows`
}

export const apiUpdateWorkflowUrl = (owner, uuid, { workflowsExtractionEnabled = false }) => {
  if (workflowsExtractionEnabled) {
    return `/api/apps/workflows/workflows/${uuid}`
  }

  return owner.type === 'organization'
    ? `/api/organization/workflows/${uuid}`
    : `/api/${owner.type}s/${owner.id}/workflows/${uuid}`
}

export const apiDeleteWorkflowUrl = (owner, uuid, { workflowsExtractionEnabled = false }) => {
  if (workflowsExtractionEnabled) {
    return `/api/apps/workflows/workflows/${uuid}`
  }

  return (
    owner.type === 'organization'
      ? `/api/organization/workflows/${uuid}`
      : `/api/${owner.type}s/${owner.id}/workflows/${uuid}`
  )
}

export const apiWorkflowShowUrl = (owner, uuid) => (
  owner.type === 'organization'
    ? `/api/organization/workflows/${uuid}`
    : `/api/${owner.type}s/${owner.id}/workflows/${uuid}`
)

export const apiWorkflowEditUrl = (owner, uuid) => (
  owner.type === 'organization'
    ? `/api/organization/workflows/${uuid}/edit`
    : `/api/${owner.type}s/${owner.id}/workflows/${uuid}/edit`
)
export const apiWorkflowNewUrl = (owner) => (
  owner.type === 'organization'
    ? '/api/organization/workflows/new'
    : `/api/${owner.type}s/${owner.id}/workflows/new`
)

export const apiOwnerEventTypesUrl = '/api/apps/workflows/event_types'

export const apiEventTypesWorkflowUrl = (type, ownerType, ownerId, eventTypeId) => (
  type === 'ManagedEventType'
    ? `/api/organization/managed_event_types/${eventTypeId}/workflows`
    : `/api/${ownerType.toLowerCase()}s/${ownerId}/event_types/${eventTypeId}/workflows`
)

/*
 * External entry point URLs
 */
export const outlookConnectedPageUrl = '/integrations/outlook/edit'

/*
 * Utility methods
 */

export const parseParams = ({ params: { owner_type: ownerType, owner_id: ownerId } }) => ({
  owner: {
    id: ownerId,
    type: ownerType === 'users' ? 'user' : 'team'
  }
})
